<lib-modal
  [title]="texts.title"
  [showCloseButton]="true"
  [showPrimaryButton]="state() === 'preview'"
  [primaryButtonText]="texts.apply"
  [disablePrimaryButton]="!canApply()"
  [showSecondaryButton]="true"
  [secondaryButtonText]="texts.cancel"
  [disableSecondaryButton]="state() === 'loading'"
  [disableCloseButton]="state() === 'loading'"
  (primaryButton)="apply()"
  (secondaryButton)="modalService.hide()"
  (closeModal)="modalService.hide()"
>
  @switch (state()) {
    @case ('upload') {
      <p>{{ texts.intro }}</p>

      <lib-input-container
        [label]="texts.fileLabel"
        [for]="'excel-import-file'"
        [helpText]="texts.fileHelp"
        [errorText]="fileError()"
      >
        <input
          id="excel-import-file"
          class="form-control"
          type="file"
          accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          [attr.aria-invalid]="fileError() ? true : null"
          [attr.aria-describedby]="
            fileError() ? 'excel-import-file-error' : null
          "
          (change)="onFileSelected($event)"
        />
      </lib-input-container>
      @if (fileError()) {
        <div id="excel-import-file-error" class="invalid-feedback d-block">
          <lib-icon name="alert-circle" size="x-small"></lib-icon>
          {{ fileError() }}
        </div>
      }

      <lib-input-container
        class="d-block mt-3"
        [label]="texts.reasoningLabel"
        [for]="'excel-import-reasoning'"
        [helpText]="texts.reasoningHelp"
      >
        <lib-vinnova-dropdown
          [id]="'excel-import-reasoning'"
          [items]="reasoningItems()"
          [ngModel]="reasoningEffort()"
          (ngModelChange)="setReasoningEffort($event)"
        ></lib-vinnova-dropdown>
      </lib-input-container>

      @if (deploymentItems.length > 0) {
        <lib-input-container
          class="d-block mt-3"
          [label]="texts.modelLabel"
          [for]="'excel-import-model'"
          [helpText]="texts.modelHelp"
        >
          <lib-vinnova-dropdown
            [id]="'excel-import-model'"
            [items]="deploymentItems"
            [ngModel]="deployment()"
            (ngModelChange)="setDeployment($event)"
          ></lib-vinnova-dropdown>
        </lib-input-container>
      }

      @if (supportsPreprocessing) {
        <lib-vinnova-checkboxes
          class="d-block mt-3 excel-import-preprocessing"
          [singleCheckbox]="true"
          [checkboxObjects]="[
            {
              id: 'excel-import-preprocessing',
              label: texts.preprocessingLabel ?? '',
            },
          ]"
          [ngModel]="preprocessing()"
          (ngModelChange)="setPreprocessing($event)"
        ></lib-vinnova-checkboxes>
        @if (texts.preprocessingHelp) {
          <p class="small text-muted mb-0">{{ texts.preprocessingHelp }}</p>
        }
      }

      <div class="d-flex justify-content-end mt-3">
        <button
          vinnova-button-primary
          type="button"
          [disabled]="!selectedFile()"
          (click)="analyze()"
        >
          {{ texts.analyze }}
        </button>
      </div>
    }

    @case ('loading') {
      <div class="text-center my-4">
        <div class="loader excel-import-loader" aria-hidden="true"></div>
        <p class="mb-1" role="status" aria-live="polite">
          {{ isSlow() ? texts.loadingSlow : texts.loading }}
        </p>
        <p class="small text-muted mb-3">
          {{ texts.elapsed }}: {{ elapsedSeconds() }} s
        </p>
        <button
          vinnova-button-secondary
          type="button"
          (click)="cancelAnalyze()"
        >
          {{ texts.cancelAnalyze }}
        </button>
      </div>
    }

    @case ('error') {
      <lib-message
        type="error"
        [message]="errorMessage() ?? texts.error"
      ></lib-message>
      <div class="d-flex justify-content-end mt-3">
        <button vinnova-button-secondary type="button" (click)="reset()">
          {{ texts.chooseOther }}
        </button>
      </div>
    }

    @case ('preview') {
      @if (supportsDebugToggle) {
        <lib-vinnova-checkboxes
          class="d-block mb-3 excel-import-debug-toggle"
          [singleCheckbox]="true"
          [checkboxObjects]="[
            {
              id: 'excel-import-debug',
              label: texts.debugLabel ?? '',
            },
          ]"
          [ngModel]="showDebug()"
          (ngModelChange)="setShowDebug($event)"
        ></lib-vinnova-checkboxes>
      }

      @if (showDebug()) {
        @for (warning of warnings(); track warning) {
          <lib-message
            type="info"
            size="small"
            [message]="warning"
            class="d-block mb-2"
          ></lib-message>
        }

        @if (summary(); as s) {
          <lib-message
            type="info"
            size="small"
            class="d-block mb-2 excel-import-summary"
          >
            <span class="fw-bold">{{ s.layoutLabel }}</span>
            – {{ texts.summaryRows }}: {{ s.rowsInPeriod }} / {{ s.rowsTotal }}
            @if (s.dateDescription) {
              · {{ texts.summaryDate }}: {{ s.dateDescription }}
            }
            @if (s.sheetsSkipped.length > 0) {
              · {{ texts.summarySheetsSkipped }}:
              {{ s.sheetsSkipped.join(', ') }}
            }
          </lib-message>
        }

        @if (timings(); as t) {
          <p class="small text-muted mb-3 excel-import-timings">
            {{ texts.timingsPreprocessing }}:
            {{ t.preprocessing | number: '1.1-1' : 'sv' }} s ·
            {{ texts.timingsExtraction }}:
            {{ t.rest | number: '1.1-1' : 'sv' }} s ·
            {{ texts.timingsServerTotal }}:
            {{ t.serverTotal | number: '1.1-1' : 'sv' }} s ·
            {{ texts.timingsRoundTrip }}: {{ t.roundTrip }} s
          </p>
        }
      }

      <div class="row align-items-end mb-4">
        <div class="col-md-6">
          <lib-input-container
            [label]="texts.unitLabel"
            [for]="'excel-import-unit'"
            [helpText]="texts.unitHelp"
          >
            <lib-vinnova-dropdown
              [id]="'excel-import-unit'"
              [items]="unitItems()"
              [ngModel]="unit()"
              (ngModelChange)="setUnit($event)"
            ></lib-vinnova-dropdown>
          </lib-input-container>
        </div>
        <div class="col-md-6 text-md-end">
          <button vinnova-button-text type="button" (click)="reset()">
            {{ texts.chooseOther }}
          </button>
        </div>
      </div>

      @for (block of preview(); track block.part.id) {
        <section class="excel-import-part mb-4">
          <div
            class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2"
          >
            <div>
              <h3 class="heading-size-5 mb-0">
                {{ block.part.namn ?? block.part.id }}
                @if (block.part.koordinerande) {
                  <span class="badge text-bg-light ms-2">{{
                    texts.koordinator
                  }}</span>
                }
                @if (
                  block.hasImportedValues &&
                  confidenceLabel(block.part.confidence) &&
                  showDebug()
                ) {
                  <span
                    class="badge ms-2"
                    [class]="confidenceClass(block.part.confidence)"
                    >{{ confidenceLabel(block.part.confidence) }}</span
                  >
                }
              </h3>
              @if (block.part.organisationsnummer) {
                <div class="small text-muted">
                  {{ texts.orgnr }}: {{ block.part.organisationsnummer }}
                </div>
              }
              @if (
                block.part.arbetsstalleNamn &&
                block.part.arbetsstalleNamn !== block.part.namn
              ) {
                <div class="small text-muted">
                  {{ texts.arbetsstalle }}: {{ block.part.arbetsstalleNamn }}
                </div>
              }
              @if (block.part.adress) {
                <div class="small text-muted">
                  {{ texts.adress }}: {{ block.part.adress }}
                </div>
              }
              @if (showDebug()) {
                @if (reconciliationFor(block.part.id); as r) {
                  <dl class="excel-import-reconciliation small mb-0">
                    <div>
                      <dt>{{ texts.reconciliationInFile }}</dt>
                      <dd>{{ r.totalInFile | number: '1.0-0' : 'sv' }}</dd>
                    </div>
                    <div>
                      <dt>{{ texts.reconciliationInPeriod }}</dt>
                      <dd>{{ r.totalInPeriod | number: '1.0-0' : 'sv' }}</dd>
                    </div>
                    <div>
                      <dt>{{ texts.reconciliationExcluded }}</dt>
                      <dd>{{ r.totalExcluded | number: '1.0-0' : 'sv' }}</dd>
                    </div>
                    <div>
                      <dt>{{ texts.reconciliationUnmatched }}</dt>
                      <dd>{{ r.totalUnmatched | number: '1.0-0' : 'sv' }}</dd>
                    </div>
                  </dl>
                }
              }
            </div>
            @if (block.hasImportedValues) {
              <lib-vinnova-checkboxes
                [singleCheckbox]="true"
                [checkboxObjects]="[
                  {
                    id: 'include-' + block.part.id,
                    label: texts.includePart,
                  },
                ]"
                [ngModel]="includedParts()[block.part.id]"
                (ngModelChange)="setIncluded(block.part.id, $event)"
              ></lib-vinnova-checkboxes>
            }
          </div>

          @if (block.hasImportedValues) {
            @if (showDebug()) {
              @if (block.part.confidenceReason) {
                <lib-message
                  type="warning"
                  size="small"
                  [message]="block.part.confidenceReason"
                  class="d-block mb-2 excel-import-confidence-reason"
                ></lib-message>
              }
              @for (warning of block.part.warnings ?? []; track warning) {
                <lib-message
                  type="info"
                  size="small"
                  [message]="warning"
                  class="d-block mb-2 excel-import-part-warning"
                ></lib-message>
              }
            }
            @for (section of block.sections; track section.title) {
              <lib-excel-import-preview-table
                [title]="section.title"
                [years]="years"
                [columnLabels]="columnLabels"
                [rows]="section.rows"
                [texts]="{
                  current: texts.current,
                  imported: texts.imported,
                  source: texts.source,
                }"
              ></lib-excel-import-preview-table>
            }
          } @else {
            <p class="text-muted mb-0">{{ texts.noValues }}</p>
          }
          @if (showDebug()) {
            @if (excludedForPart(block.part.id); as partExcluded) {
              @if (partExcluded.length > 0) {
                <lib-excel-import-excluded-table
                  [title]="texts.excludedTitle ?? ''"
                  [entries]="partExcluded"
                  [texts]="excludedTexts()"
                ></lib-excel-import-excluded-table>
              }
            }
          }
        </section>
      }

      @if (showDebug()) {
        @if (excludedWithoutPart().length > 0) {
          <lib-excel-import-excluded-table
            [title]="texts.excludedWorkbookTitle ?? ''"
            [entries]="excludedWithoutPart()"
            [texts]="excludedTexts()"
          ></lib-excel-import-excluded-table>
        }

        @if (unmatchedBlocks().length > 0) {
          <lib-message type="warning" [title]="texts.unmatchedTitle">
            <ul class="mb-0">
              @for (block of unmatchedBlocks(); track block) {
                <li>{{ block }}</li>
              }
            </ul>
          </lib-message>
        }
      }
    }
  }
</lib-modal>
